// DEBT-VITA™ — 6 KPI cards vitalistes
import React from "react";
import {
  Coins,
  CalendarDays,
  Calendar,
  Baby,
  Globe,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

function fmtNum(v, opts = {}) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const { digits = 2, sep = "fr-FR" } = opts;
  return Number(v).toLocaleString(sep, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits === 0 ? 0 : 0,
  });
}

function fmtMoney(v, currency = "UM") {
  if (v === null || v === undefined) return "—";
  return `${fmtNum(v, { digits: 0 })} ${currency}`;
}

function tavdColor(t) {
  if (t === null || t === undefined) return "text-muted-foreground";
  if (t < 100) return "text-emerald-600 dark:text-emerald-400";
  if (t <= 300) return "text-orange-600 dark:text-orange-400";
  return "text-red-700 dark:text-red-400";
}

function KPI({ Icon, title, value, sub, color = "text-foreground", testId }) {
  return (
    <Card data-testid={testId} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          {title}
        </div>
        <div className={"mt-2 font-mono text-2xl font-extrabold leading-tight " + color}>
          {value}
        </div>
        {sub ? (
          <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {sub}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function DebtKPICards({ results, debtParams }) {
  if (!results) return null;
  const currency = debtParams?.currency || "UM";
  const horizonYear =
    new Date().getFullYear() + (debtParams?.projection_years || 30);
  const totalLifeDaysB = results.dette_vie_nationale_totale
    ? results.dette_vie_nationale_totale / 1_000_000_000
    : null;
  const totalLifeYearsM = results.annees_vie_collectives
    ? results.annees_vie_collectives / 1_000_000
    : null;
  const tavd = results.tavd;
  const tavdRatio = tavd ? tavd / 100 : null;

  return (
    <div
      className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3"
      data-testid="debt-kpi-grid"
    >
      <KPI
        Icon={Coins}
        title="Dette par contribuable"
        value={fmtMoney(results.dette_par_tete_um, currency)}
        sub={
          results.jours_vie_dette
            ? `soit ${fmtNum(results.jours_vie_dette, { digits: 1 })} jours de sa vie hypothéqués`
            : null
        }
        testId="kpi-debt-per-capita"
      />
      <KPI
        Icon={CalendarDays}
        title="Années de vie hypothéquées"
        value={
          results.annees_vie_dette
            ? `${fmtNum(results.annees_vie_dette, { digits: 2 })} ans`
            : "—"
        }
        sub={
          results.pct_carriere_dette
            ? `${fmtNum(results.pct_carriere_dette, { digits: 2 })} % de la carrière active aliénée à la dette`
            : null
        }
        testId="kpi-life-years-mortgaged"
      />
      <KPI
        Icon={Calendar}
        title="Jours/an pour rembourser"
        value={
          results.jours_vie_service_annuel
            ? `${fmtNum(results.jours_vie_service_annuel, { digits: 1 })} j/an`
            : "—"
        }
        sub={
          results.jours_vie_interets !== null &&
          results.jours_vie_capital !== null
            ? `dont ${fmtNum(results.jours_vie_interets, {
                digits: 1,
              })} j intérêts + ${fmtNum(results.jours_vie_capital, {
                digits: 1,
              })} j capital`
            : null
        }
        testId="kpi-yearly-service"
      />
      <KPI
        Icon={Baby}
        title="Charge enfant né aujourd'hui"
        value={
          results.annees_vie_enfant
            ? `${fmtNum(results.annees_vie_enfant, { digits: 2 })} ans`
            : "—"
        }
        sub={`à l'horizon ${horizonYear}`}
        testId="kpi-child-burden"
      />
      <KPI
        Icon={Globe}
        title="Dette nationale en vies"
        value={
          totalLifeDaysB
            ? `${fmtNum(totalLifeDaysB, { digits: 2 })} Md j`
            : "—"
        }
        sub={
          totalLifeYearsM
            ? `soit ${fmtNum(totalLifeYearsM, { digits: 2 })} millions d'années de vie collective`
            : null
        }
        testId="kpi-national-life-debt"
      />
      <KPI
        Icon={TrendingUp}
        title="TAVD — Taux d'Aliénation"
        value={tavd !== null ? `${fmtNum(tavd, { digits: 1 })} %` : "—"}
        sub={
          tavdRatio !== null
            ? `La dette = ${fmtNum(tavdRatio, { digits: 2 })}× la pression fiscale annuelle en vies`
            : null
        }
        color={tavdColor(tavd)}
        testId="kpi-tavd"
      />
    </div>
  );
}
